import {
  MAX_PROVIDER_DOCUMENTS,
  PROVIDER_DOCUMENT_TYPES,
  UPLOAD_RULES,
  type ProviderDocumentInfo,
  type ProviderDocumentType,
} from '@hemmafixa/shared';
import { FileGallery, FileUpload, toast, type FileGalleryItem } from '@hemmafixa/ui';
import { useCallback } from 'react';
import { fill, useI18n } from '@/i18n/context';
import { galleryItem, uploadErrorMessage, useFileTexts, useFileUploads } from '../shared/file-uploads';
import { QueryView } from '../shared/query-view';
import { useProviderDocumentActions, useProviderDocuments } from './api';

const RULES = UPLOAD_RULES.provider_document;

/** The company's documents by type: company registration, F-skatt certificate, other (P2-09 / P3-07). */
export function ProviderDocuments({ editable }: { editable: boolean }) {
  const query = useProviderDocuments();
  return (
    <QueryView query={query} loading="detail">
      {(documents) => (
        <div className="grid gap-4">
          {PROVIDER_DOCUMENT_TYPES.map((type) => (
            <DocumentType
              key={type}
              type={type}
              documents={documents.filter((document) => document.type === type)}
              total={documents.length}
              editable={editable}
            />
          ))}
        </div>
      )}
    </QueryView>
  );
}

function DocumentType({
  type,
  documents,
  total,
  editable,
}: {
  type: ProviderDocumentType;
  documents: ProviderDocumentInfo[];
  total: number;
  editable: boolean;
}) {
  const { t } = useI18n();
  const s = t.provider.onboarding;
  const f = t.files;
  const texts = useFileTexts();
  const actions = useProviderDocumentActions();
  const { pending, upload, dismiss } = useFileUploads({
    create: useCallback(
      (file: File) =>
        actions.create({ type, fileName: file.name, contentType: file.type, sizeBytes: file.size }),
      [actions, type],
    ),
    complete: actions.complete,
    onSettled: actions.refresh,
    errorMessage: useCallback((error: unknown) => uploadErrorMessage(error, f.failed, f.wrongFile), [f]),
  });
  const room = MAX_PROVIDER_DOCUMENTS - total - pending.filter((item) => item.status === 'uploading').length;
  const items = [...documents.map(galleryItem), ...pending];

  const onRemove = (item: FileGalleryItem) => {
    if (item.id.startsWith('local-')) return dismiss(item.id);
    actions
      .remove(item.id)
      .then(() => {
        toast.success(f.removed);
        actions.refresh();
      })
      .catch(() => toast.error(f.removeFailed));
  };

  return (
    <div className="grid gap-2 rounded-lg border border-border p-4">
      <div className="grid gap-0.5">
        <h3 className="text-sm font-semibold">{s.docTypes[type]}</h3>
        <p className="text-xs text-muted-foreground">{s.docTypeHints[type]}</p>
      </div>
      {items.length > 0 && (
        <FileGallery items={items} labels={texts.gallery} onRemove={editable ? onRemove : undefined} />
      )}
      {editable && room > 0 && (
        <FileUpload
          accept={[...RULES.types]}
          maxSizeBytes={RULES.maxBytes}
          maxFiles={room}
          files={[]}
          onFilesChange={(chosen) => void upload(chosen)}
          onReject={(message) => toast.error(message)}
          label={fill(s.docDrop, { document: s.docTypes[type].toLowerCase() })}
          hint={s.docHint}
          labels={texts.upload}
        />
      )}
    </div>
  );
}
